/**
 * Grid state persistor - attaches to Kendo Grid events and persists/restores state
 */

import { CONFIG } from '../../core/config.js';
import { logError, logDebug, safeExecute } from '../../core/error-handler.js';
import { debounce } from '../../core/utils.js';
import { Grid as GridHelpers } from '../kendo-helpers.js';
import { GridStateStorage } from './session-storage.js';

function pickPersistedState(grid) {
    const state = grid.getOptions();

    // Defensive guards
    const ds = state && state.dataSource ? state.dataSource : {};

    // Remove values from fields (Externists rationale)
    const fields = Array.isArray(ds.fields) ? ds.fields.map(f => ({ ...f })) : [];
    for (let i = 0; i < fields.length; i++) {
        if (fields[i] && Object.prototype.hasOwnProperty.call(fields[i], 'values')) {
            delete fields[i].values;
        }
    }

    // Capture table width workaround
    const tableWidth =
        grid && grid.table && typeof grid.table.width === 'function'
            ? grid.table.width()
            : undefined;

    // Sanitize columns: preserve visibility flags (hidden) for column chooser functionality
    const sanitizeColumns = cols => {
        if (!Array.isArray(cols)) return undefined;
        return cols.map(col => {
            const clone = { ...col };
            // Keep hidden property for column chooser persistence
            // if (Object.prototype.hasOwnProperty.call(clone, 'hidden')) {
            //     delete clone.hidden;
            // }
            return clone;
        });
    };

    // Build safe dataSource state
    const dataSourceState = {
        fields: fields
    };

    // Only include valid, non-null values
    if (ds.page && ds.page > 0) {
        dataSourceState.page = ds.page;
    }
    if (ds.pageSize && ds.pageSize > 0) {
        dataSourceState.pageSize = ds.pageSize;
    }
    if (ds.sort && Array.isArray(ds.sort) && ds.sort.length > 0) {
        dataSourceState.sort = ds.sort;
    }
    // Skip filter persistence - handled by FilterManager
    // if (ds.filter && ds.filter.filters && Array.isArray(ds.filter.filters) && ds.filter.filters.length > 0) {
    //     dataSourceState.filter = ds.filter;
    // }
    if (ds.group && Array.isArray(ds.group) && ds.group.length > 0) {
        dataSourceState.group = ds.group;
    }
    if (ds.columns && Array.isArray(ds.columns) && ds.columns.length > 0) {
        dataSourceState.columns = sanitizeColumns(ds.columns);
    }

    return {
        dataSource: dataSourceState,
        columns: sanitizeColumns(grid.columns),
        tableWidth: tableWidth
    };
}

function captureDefaultState(grid) {
    // Snapshot current grid options as defaults, but clear data-related params
    const state = grid.getOptions();
    const ds = state && state.dataSource ? state.dataSource : {};

    const defaultPageSize = ds.pageSize && ds.pageSize > 0 ? ds.pageSize : undefined;
    const columns = Array.isArray(state.columns)
        ? state.columns.map(col => {
              const clone = { ...col };
              // Keep hidden property for column chooser persistence
              // if (Object.prototype.hasOwnProperty.call(clone, 'hidden')) delete clone.hidden;
              return clone;
          })
        : undefined;

    // Capture default sort from dataSource configuration
    const defaultSort =
        ds.sort && Array.isArray(ds.sort) && ds.sort.length > 0 ? ds.sort : undefined;

    return {
        columns,
        page: 1,
        pageSize: defaultPageSize,
        sort: defaultSort
    };
}

function injectResetButton(grid, selector, defaultState) {
    const $wrapper = grid.wrapper || $(grid.element).closest('.k-grid');
    if ($wrapper.length === 0) return;

    // Create container after the entire grid
    let $container = $wrapper.next('.k-grid-reset-container');
    if ($container.length === 0) {
        $container = $(
            '<div class="k-grid-reset-container" style="margin-top: 8px; display: flex; justify-content: flex-end;"></div>'
        );
        $wrapper.after($container);
    }

    // Create (or find) button element
    let $btn = $container.find('.k-grid-reset-state');
    if ($btn.length === 0) {
        $btn = $(
            '<button type="button" class="k-grid-reset-state btn btn-outline-secondary btn-sm" title="Obnovit výchozí nastavení"><i class="fas fa-undo"></i> <span>Obnovit výchozí</span></button>'
        );
        $container.append($btn);
    }

    const updateVisibility = () => {
        const hasState = !!GridStateStorage.loadForSelector(selector);
        if (hasState) {
            $btn.show();
        } else {
            $btn.hide();
        }
    };

    $btn.off('click.gridReset').on('click.gridReset', e => {
        e.preventDefault();
        try {
            // Remove persisted state
            GridStateStorage.removeForSelector(selector);

            // Apply default columns if available
            if (defaultState.columns) {
                try {
                    grid.setOptions({ columns: defaultState.columns });
                } catch (_) {
                    /* ignore */
                }
            }

            // Query defaults: page 1, default pageSize, and default sort
            const query = { page: defaultState.page || 1 };
            if (defaultState.pageSize && defaultState.pageSize > 0) {
                query.pageSize = defaultState.pageSize;
            }
            if (
                defaultState.sort &&
                Array.isArray(defaultState.sort) &&
                defaultState.sort.length > 0
            ) {
                query.sort = defaultState.sort;
            }
            grid.dataSource.query(query);

            // Hide button after reset
            $btn.hide();
        } catch (error) {
            logError(error);
        }
    });

    // Initial visibility
    updateVisibility();

    return { updateVisibility };
}

function restoreState(grid, saved) {
    try {
        if (!saved) return false;

        logDebug('GridPersistence: Restoring state', { saved });

        // Restore table width if present
        if (saved.tableWidth && grid && grid.table && grid.table.width) {
            grid.table.width(saved.tableWidth);
        }

        // Apply column options first (does not trigger data read)
        if (saved.columns && Array.isArray(saved.columns)) {
            try {
                grid.setOptions({ columns: saved.columns });
            } catch (e) {
                logError(e);
            }
        }

        // Build a single query to avoid multiple reads
        if (saved.dataSource) {
            const src = saved.dataSource;
            const query = {};

            if (src.pageSize && src.pageSize > 0) {
                query.pageSize = src.pageSize;
            }
            if (src.page && src.page > 0) {
                query.page = src.page;
            }
            if (src.sort && Array.isArray(src.sort) && src.sort.length > 0) {
                const validSorts = src.sort.filter(s => s && s.field && s.dir);
                if (validSorts.length > 0) {
                    query.sort = validSorts;
                }
            }
            // Skip filter restoration - handled by FilterManager
            // if (src.filter && src.filter.filters && Array.isArray(src.filter.filters) && src.filter.filters.length > 0) {
            //     query.filter = src.filter;
            // }
            if (src.group && Array.isArray(src.group) && src.group.length > 0) {
                const validGroups = src.group.filter(g => g && g.field);
                if (validGroups.length > 0) {
                    query.group = validGroups;
                }
            }

            logDebug('GridPersistence: Executing single dataSource.query', { query });
            grid.dataSource.query(query);
        }

        return true;
    } catch (error) {
        logError(error);
        return false;
    }
}

export function attachGridStatePersistence(selector) {
    if (!CONFIG.GRID.PERSISTENCE_ENABLED) {
        logDebug('GridPersistence: Disabled by config');
        return;
    }
    if (!GridStateStorage.isAvailable()) {
        logDebug('GridPersistence: SessionStorage not available');
        return;
    }

    return safeExecute(() => {
        logDebug('GridPersistence: Attaching to', { selector });

        // Wait for grid to be fully initialized
        const checkGrid = () => {
            const instance = GridHelpers.ensure(selector);
            if (!instance.isAvailable()) {
                logDebug('GridPersistence: Grid not ready yet, retrying...');
                setTimeout(checkGrid, 100);
                return;
            }

            const grid = instance.widget;
            logDebug('GridPersistence: Grid found, attaching events');

            // Capture default state (columns + base paging) before any mutations
            const defaultState = captureDefaultState(grid);

            // Inject reset button controller
            const resetUi = injectResetButton(grid, selector, defaultState);

            // Restore once after grid initialization
            let restored = false;
            const tryRestore = () => {
                if (restored) return { applied: false, queried: false };
                logDebug('GridPersistence: Attempting to restore state');
                const saved = GridStateStorage.loadForSelector(selector);
                if (saved) {
                    logDebug('GridPersistence: Found saved state', { saved });
                    const applied = restoreState(grid, saved);
                    restored = true;
                    logDebug('GridPersistence: State restored', { applied });
                    const queried = !!(
                        saved.dataSource &&
                        ((saved.dataSource.page && saved.dataSource.page > 0) ||
                            (saved.dataSource.pageSize && saved.dataSource.pageSize > 0) ||
                            (Array.isArray(saved.dataSource.sort) &&
                                saved.dataSource.sort.length > 0) ||
                            (saved.dataSource.filter &&
                                Array.isArray(saved.dataSource.filter.filters) &&
                                saved.dataSource.filter.filters.length > 0) ||
                            (Array.isArray(saved.dataSource.group) &&
                                saved.dataSource.group.length > 0))
                    );
                    return { applied, queried };
                } else {
                    restored = true; // nothing to restore
                    logDebug('GridPersistence: No saved state found');
                    return { applied: false, queried: false };
                }
            };

            // Debounced saver with logging
            const saveNow = () => {
                logDebug('GridPersistence: Saving state');
                const state = pickPersistedState(grid);
                logDebug('GridPersistence: State to save', { state });
                const success = GridStateStorage.saveForSelector(selector, state);
                logDebug('GridPersistence: Save result', { success });
                if (resetUi && typeof resetUi.updateVisibility === 'function') {
                    resetUi.updateVisibility();
                }
            };
            const saveDebounced = debounce(saveNow, CONFIG.GRID.SAVE_DEBOUNCE_MS);

            // Restore state immediately when grid is ready
            const restoreResult = tryRestore();
            console.warn('[GridPersistence] tryRestore result:', restoreResult, 'saved state:', GridStateStorage.loadForSelector(selector));
            if (restoreResult.queried) {
                // Persistence already triggered a query (with restored page/sort).
                // Signal FilterManager to skip its initial read.
                grid._persistenceQueried = true;
                console.warn('[GridPersistence] Set _persistenceQueried=true, page=' + grid.dataSource.page());
            } else {
                // No saved state. Let FilterManager handle the first read if present,
                // otherwise trigger one ourselves.
                if (grid._filterManager) {
                    logDebug(
                        'GridPersistence: FilterManager attached, skipping initial read (FilterManager will trigger it)'
                    );
                } else {
                    logDebug(
                        'GridPersistence: No persisted query present, issuing single dataSource.read()'
                    );
                    grid.dataSource.read();
                }
            }

            // Wire events for saving changes
            grid.bind('columnResize', () => {
                logDebug('GridPersistence: columnResize event');
                saveDebounced();
            });
            grid.bind('columnReorder', () => {
                logDebug('GridPersistence: columnReorder event');
                saveDebounced();
            });
            grid.bind('columnShow', () => {
                logDebug('GridPersistence: columnShow event');
                saveDebounced();
            });
            grid.bind('columnHide', () => {
                logDebug('GridPersistence: columnHide event');
                saveDebounced();
            });
            grid.bind('sort', () => {
                logDebug('GridPersistence: sort event');
                saveDebounced();
            });
            // Skip filter events - handled by FilterManager
            // grid.bind('filter', () => { logDebug('GridPersistence: filter event'); saveDebounced(); });
            grid.bind('group', () => {
                logDebug('GridPersistence: group event');
                saveDebounced();
            });
            grid.bind('page', () => {
                logDebug('GridPersistence: page event');
                saveDebounced();
            });

            // Note: no dataBound handler to avoid extra reads during initial load

            logDebug('GridPersistence: Events attached successfully');
        };

        checkGrid();
    }, 'attachGridStatePersistence');
}

export const GridPersistence = {
    attach: attachGridStatePersistence
};

export default GridPersistence;
